import React from "react"
import { Divider, Box } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { KNOWLEDGE_CONTENT_TYPE } from "../../data"
import { KnowledgeContentTypeIcon } from "./KnowledgeContentTypeIcon"
import { DownloadList } from "../Uploader"
import { RowBox } from "../Boxes"
import { KnowledgeArticleContentImage } from "./KnowledgeArticleContentImage"
import { KnowledgeArticleContentVideo } from "./KnowledgeArticleContentVideo"
import { KnowledgeArticleContentText } from "./KnowledgeArticleContentText"
import { useDeviceUtils } from "../../utils"

const { TEXT, IMAGE, FILES, NOTE, NOTE_IMPORTANT, NOTE_DANGER, VIDEO } = KNOWLEDGE_CONTENT_TYPE

const useContentStyles = makeStyles((theme) => ({
  description: {
    fontSize: 16,
    lineHeight: "20px",
    whiteSpace: "pre-wrap",
    color: theme.palette.text.main,
  },
  displayContent: {
    fontSize: 16,
    lineHeight: "24px",
    // whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginBottom: 40,
  },
  displayHeading: {
    fontSize: 20,
    fontWeight: "600",
    color: theme.palette.kb.heading,
  },
}))

const useNoteStyles = makeStyles((theme) => ({
  note: {
    backgroundColor: theme.palette.notes.main.backgroundColor,
    color: theme.palette.notes.main.color,
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(3)}px`,
    borderRadius: 4,
  },
  [`note${NOTE_IMPORTANT}`]: {
    backgroundColor: theme.palette.notes.important.backgroundColor,
    color: theme.palette.notes.important.color,
  },
  [`note${NOTE_DANGER}`]: {
    backgroundColor: theme.palette.notes.danger.backgroundColor,
    color: theme.palette.notes.danger.color,
  },
  noteHeading: {
    fontWeight: "600",
    fontSize: 16,
  },
}))

const KnowledgeArticleContent = ({ content }) => {
  const classes = useContentStyles()
  const { type } = content

  return (
    <>
      {type === TEXT && <DisplayText classes={classes} content={content} />}
      {type === IMAGE && <DisplayImage classes={classes} content={content} />}
      {[NOTE, NOTE_DANGER, NOTE_IMPORTANT].includes(type) && <DisplayNote classes={classes} content={content} />}
      {type === FILES && <DisplayFiles classes={classes} content={content} />}
      {type === VIDEO && <DisplayVideo classes={classes} content={content} />}
    </>
  )
}

const DisplayText = ({ classes, content: { id, heading, value } }) => (
  <Box key={id} id={id} className={classes.displayContent}>
    {heading && (
      <Box mb={2} className={classes.displayHeading}>
        {heading}
      </Box>
    )}
    <KnowledgeArticleContentText value={value} />
  </Box>
)

const DisplayImage = ({ classes, content }) => {
  const { canPostMessage, postImagesMessage } = useDeviceUtils()
  const { id, heading, uploads } = content

  const handleImageClick = (index) => {
    if (!canPostMessage()) return

    postImagesMessage(uploads, index)
  }

  if (!heading && !uploads?.length) {
    return null
  }

  return (
    <Box key={id} id={id} className={classes.displayContent}>
      {heading && (
        <Box mb={2} className={classes.displayHeading}>
          {heading}
        </Box>
      )}
      {uploads.map((upload, index) => (
        <KnowledgeArticleContentImage key={upload.id} upload={upload} onImageClick={() => handleImageClick(index)} />
      ))}
    </Box>
  )
}

const DisplayFiles = ({ classes, content }) => {
  const { id, heading, uploads } = content

  if (!heading && !uploads?.length) {
    return null
  }

  return (
    <Box key={id} id={id} className={classes.displayContent}>
      {heading && (
        <>
          <Box mb={2} className={classes.displayHeading}>
            {heading}
          </Box>
          <Box mb={3}>
            <Divider />
          </Box>
        </>
      )}
      <DownloadList uploads={uploads} variant="cards" />
    </Box>
  )
}

const DisplayNote = ({ classes, content }) => {
  const noteClasses = useNoteStyles()
  const { id, type, heading, value } = content

  if (!heading && !value) {
    return null
  }

  return (
    <Box key={id} id={id} className={classes.displayContent}>
      <Box className={`${noteClasses.note} ${noteClasses[`note${type}`]}`}>
        <RowBox mb={1}>
          <KnowledgeContentTypeIcon type={type} />
          <Box ml={0.5} className={noteClasses.noteHeading}>
            {heading}
          </Box>
        </RowBox>
        <Box className={noteClasses.description}>
          <KnowledgeArticleContentText value={value} />
        </Box>
      </Box>
    </Box>
  )
}

const DisplayVideo = ({ classes, content }) => {
  const { id, heading, value } = content

  if (!heading && !value) {
    return null
  }

  return (
    <Box key={id} id={id} className={classes.displayContent}>
      {heading && (
        <Box mb={2} className={classes.displayHeading}>
          {heading}
        </Box>
      )}

      {value && (
        <RowBox justifyContent="center">
          <KnowledgeArticleContentVideo value={value} />
        </RowBox>
      )}
    </Box>
  )
}

export { KnowledgeArticleContent }
